import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ProductSearch = ({ onSearch, onFilterToggle, className = "" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);

  // Mock search suggestions
  const mockSuggestions = [
    { id: 1, text: "SPATZ3 Adjustable Balloon", category: "Obesity Management", type: "product" },
    { id: 2, text: "Hanarostent Esophagus", category: "Surgery", type: "product" },
    { id: 3, text: "Polypectomy Snare", category: "Endoscopy", type: "product" },
    { id: 4, text: "PEG Kit", category: "Surgery", type: "product" },
    { id: 5, text: "Olympus", category: "Manufacturer", type: "brand" },
    { id: 6, text: "Endoscopy", category: "Category", type: "category" },
    { id: 7, text: "FDA Approved", category: "Certification", type: "filter" },
    { id: 8, text: "Gastroenterology", category: "Specialty", type: "specialty" }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    
    // Call onSearch immediately for real-time filtering
    if (onSearch) {
      onSearch(query);
    }
    
    if (query?.length > 0) {
      const filtered = mockSuggestions?.filter(suggestion =>
        suggestion?.text?.toLowerCase()?.includes(query?.toLowerCase()) ||
        suggestion?.category?.toLowerCase()?.includes(query?.toLowerCase())
      );
      setSuggestions(filtered?.slice(0, 6));
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      onSearch(searchQuery?.trim());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion?.text);
    onSearch(suggestion?.text);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onSearch('');
    setShowSuggestions(false);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'product': return 'Package';
      case 'brand': return 'Building';
      case 'category': return 'Layers';
      case 'filter': return 'Filter';
      case 'specialty': return 'Stethoscope';
      default: return 'Search';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'product': return 'text-primary';
      case 'brand': return 'text-success';
      case 'category': return 'text-warning';
      case 'filter': return 'text-accent';
      case 'specialty': return 'text-secondary';
      default: return 'text-text-secondary';
    }
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      {/* Search Form */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="relative">
          <Input
            type="search"
            placeholder="Search products, manufacturers, categories..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery?.length > 0 && setShowSuggestions(true)}
            className="pl-12 pr-12 h-12 text-base"
          />
          
          {/* Search Icon */}
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
            <Icon name="Search" size={20} className="text-text-secondary" />
          </div>

          {/* Clear Button */}
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors duration-200"
              aria-label="Clear search"
            >
              <Icon name="X" size={16} className="text-text-secondary" />
            </button>
          )}
        </div>

        {/* Mobile Filter Toggle */}
        <button
          type="button"
          onClick={onFilterToggle}
          className="lg:hidden absolute right-16 top-1/2 transform -translate-y-1/2 p-2 hover:bg-muted rounded-lg transition-colors duration-200"
          aria-label="Toggle filters"
        >
          <Icon name="SlidersHorizontal" size={18} className="text-text-secondary" />
        </button>
      </form>
      {/* Search Suggestions */}
      <AnimatePresence>
        {showSuggestions && suggestions?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-medical-hover z-50 overflow-hidden"
          >
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-text-secondary uppercase tracking-wide border-b border-border">
                Suggestions
              </div>
              
              {suggestions?.map((suggestion) => (
                <button
                  key={suggestion?.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className={`flex-shrink-0 ${getTypeColor(suggestion?.type)}`}>
                    <Icon name={getTypeIcon(suggestion?.type)} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {suggestion?.text}
                    </div>
                    <div className="text-xs text-text-secondary">
                      in {suggestion?.category}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Icon name="ArrowUpRight" size={14} className="text-text-secondary" />
                  </div>
                </button>
              ))}
              
              {/* Search All Results */}
              <div className="border-t border-border mt-2">
                <button
                  onClick={handleSearchSubmit}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className="flex-shrink-0 text-primary">
                    <Icon name="Search" size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-primary">
                      Search for "{searchQuery}"
                    </div>
                    <div className="text-xs text-text-secondary">
                      View all results
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Popular Searches (when no query) */}
      <AnimatePresence>
        {showSuggestions && searchQuery?.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-medical-hover z-50 overflow-hidden"
          >
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-text-secondary uppercase tracking-wide border-b border-border">
                Popular Searches
              </div>
              
              {mockSuggestions?.slice(0, 4)?.map((suggestion) => (
                <button
                  key={suggestion?.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors duration-200 flex items-center space-x-3"
                >
                  <div className={`flex-shrink-0 ${getTypeColor(suggestion?.type)}`}>
                    <Icon name={getTypeIcon(suggestion?.type)} size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text-primary">
                      {suggestion?.text}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {suggestion?.category}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductSearch;